import React from 'react';
import { Section, Description, ContentItem } from './types';
import { useTranslation } from 'react-i18next';
import Badge from '../../shared/TailwindComponents/Badge';
import { Well } from '../../shared/brand/Field';
import { sanitizeHTML } from '../../../utils/security/htmlSanitizer';

interface HelpContentProps {
  content?: {
    section: Section;
    description: Description;
  };
}

const HelpContent: React.FC<HelpContentProps> = ({ content }) => {
  const { t } = useTranslation();

  if (!content) {
    return <div>{t('No content available')}</div>;
  }

  const { section, description } = content;

  const renderContent = (contentItems: ContentItem[]) => {
    const renderedContent = [];
    let subInfoItems: ContentItem[] = [];

    contentItems.forEach((item, index) => {
      if (item.type === 'MainInfo') {
        if (subInfoItems.length > 0) {
          renderedContent.push(renderSubInfoGroup(subInfoItems));
          subInfoItems = [];
        }
        renderedContent.push(
          <div key={`maininfo-${index}`} className="mb-6">
            {item.header && <h4 className="text-base font-semibold mb-2">{item.header}</h4>}
            <div className="text-fg-body">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              ></p>
            </div>
            {item.sub_content && renderSubContent(item.sub_content)}
          </div>
        );
      } else if (item.type === 'SubInfo') {
        subInfoItems.push(item);
      } else {
        if (subInfoItems.length > 0) {
          renderedContent.push(renderSubInfoGroup(subInfoItems));
          subInfoItems = [];
        }
        renderedContent.push(renderContentItem(item, index));
      }
    });

    if (subInfoItems.length > 0) {
      renderedContent.push(renderSubInfoGroup(subInfoItems));
    }

    return renderedContent;
  };

  const renderSubInfoGroup = (subInfoItems: ContentItem[]) => {
    return (
      <div key={`subinfo-group-${subInfoItems[0].header || subInfoItems[0].text}`} className="mb-6">
        {subInfoItems.map((item, idx) => (
          <div key={`subinfo-${idx}`} className="mb-4">
            {item.header && <h5 className="text-base font-semibold mb-2">{item.header}</h5>}
            <div className="text-fg-body">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              ></p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderContentItem = (item: ContentItem, index: number) => {
    // Intro/SubInfo are running prose and stay on the page surface; everything
    // else is a discrete block and gets the standard sunken well.
    const isProse = item.type === 'Intro' || item.type === 'SubInfo';
    const Container = isProse ? 'div' : Well;

    return (
      <Container key={index} className="mb-6">
      <div>
        <div className="flex items-center justify-between mb-2 flex-1">
          {item.header && (
            <h4 className="text-lg font-semibold">
              {item.header}
            </h4>
          )}
          {item.status && (
            <Badge
              color={item.status === 'Coming soon' ? 'yellow' : 'green'}
              className="ml-2"
            >
              {t(item.status)}
            </Badge>
          )}
        </div>
        <div className="text-fg-body">
          {item.type === 'Info' && (
            <p
              dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
              className="leading-relaxed text-sm"
            ></p>
          )}
          {item.type === 'Intro' && (
            <p
              dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
              className="leading-relaxed text-sm"
            ></p>
          )}
          {item.type === 'Tips' && (
            <div className="border-l-4 border-champagne pl-4">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed italic text-sm"
              ></p>
            </div>
          )}
          {item.type === 'Version' && (
            <div className="italic text-sm">
              <p dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}></p>
            </div>
          )}
          {item.type === 'Action' && (
            <div className="mt-2">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              ></p>
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-champagne hover:text-champagne/80"
                >
                  {t('Learn more')}
                </a>
              )}
            </div>
          )}
          {item.type === 'Feature' && (
            <>
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              ></p>
              {item.usage && (
                <div className="mt-4">
                  <label className="font-semibold">{t('Usage')}</label>
                  <p
                    dangerouslySetInnerHTML={{ __html: formatText(item.usage || '') }}
                    className="leading-relaxed text-sm"
                  ></p>
                </div>
              )}
              {item.sub_content && renderSubContent(item.sub_content)}
            </>
          )}
          {(item.type === 'Service' || item.type === 'Tips') && item.usage && (
            <div className="mt-4">
              <p>
                <strong>{t('Functionality')}: </strong>
                {item.functionality || ''}
              </p>
              <div className="mt-2">
                <label className="font-semibold">{t('Usage')}:</label>
                <p
                  dangerouslySetInnerHTML={{ __html: formatText(item.usage || '') }}
                  className="leading-relaxed text-sm"
                ></p>
              </div>
            </div>
          )}
        </div>
      </div>
      </Container>
    );
  };

  const renderSubContent = (subItems: ContentItem[]) => {
    return subItems.map((subItem, idx) => (
      <div key={idx} className="ml-4 mt-4">
        {subItem.header && <h5 className="text-lg font-semibold mb-2">{subItem.header}</h5>}
        <div className="text-fg-body">
          <p
            dangerouslySetInnerHTML={{ __html: formatText(subItem.text || '') }}
            className="leading-relaxed text-sm"
          ></p>
        </div>
      </div>
    ));
  };

  // The manual is backend prose, so the markup this builds out of it goes
  // through the app's sanitizer before reaching dangerouslySetInnerHTML.
  const formatText = (text: string) =>
    sanitizeHTML(
      text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br />')
    );

  return (
    <div className="p-4 overflow-y-auto h-full custom-scrollbar">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">{section.main_title}</h2>
        <h3 className="text-base text-fg-body">{description.title}</h3>
      </div>
      <div>{renderContent(description.content)}</div>
    </div>
  );
};

export default HelpContent;
